/**
 * IA Console — Uso sub-tab (OpenSpec #2.8).
 *
 * Mostra KPIs agregados + breakdown por agente + top 20 chamadas mais caras
 * + botão de export CSV. Filtros: período (7d / 30d), agente, tier.
 *
 * Super-admin tem um campo extra de "Organização" para drill-down cross-org.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  exportUsageCsv,
  getUsageStats,
  IaUsageAgent,
  ModelTier,
  UsageQueryResponse,
} from '../../services/iaUsage';

const AGENT_OPTIONS: { value: IaUsageAgent; label: string }[] = [
  { value: 'orchestrator', label: 'Orchestrator' },
  { value: 'guardrails', label: 'Guardrails' },
  { value: 'handoff-classifier', label: 'Handoff classifier' },
  { value: 'price-intent-classifier', label: 'Price intent' },
  { value: 'reactivation-classifier', label: 'Reativação' },
  { value: 'sdr-conversation', label: 'SDR conversation' },
  { value: 'field-extractor', label: 'Field extractor' },
  { value: 'crm-manager', label: 'CRM manager' },
  { value: 'person-detector', label: 'Person detector' },
  { value: 'other', label: 'Outro' },
];

const TIER_OPTIONS: { value: ModelTier; label: string }[] = [
  { value: 'fast', label: 'Rápido' },
  { value: 'standard', label: 'Padrão' },
  { value: 'quality', label: 'Qualidade' },
];

type PeriodPreset = '7d' | '30d';

function isoDaysAgo(days: number): string {
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
}

function periodRange(preset: PeriodPreset): { from: string; to: string } {
  return { from: isoDaysAgo(preset === '7d' ? 7 : 30), to: new Date().toISOString() };
}

function fmtUsd(v: number): string {
  return `US$ ${v.toFixed(4)}`;
}

function fmtInt(v: number): string {
  return v.toLocaleString('pt-BR');
}

const cardStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 10,
  padding: '14px 18px',
  border: '1px solid #e2e8f0',
  minWidth: 180,
  flex: '1 1 200px',
};

const kpiLabel: React.CSSProperties = {
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: 0.6,
  color: '#64748b',
  fontWeight: 700,
  marginBottom: 6,
};

const kpiValue: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 700,
  color: '#0f172a',
};

const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  fontSize: 13,
  background: '#fff',
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#64748b',
  marginBottom: 4,
  fontWeight: 600,
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  overflow: 'hidden',
  fontSize: 13,
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  background: '#f1f5f9',
  borderBottom: '1px solid #e2e8f0',
  fontWeight: 700,
  color: '#475569',
};

const tdStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderBottom: '1px solid #f1f5f9',
  color: '#0f172a',
};

export const UsoTab: React.FC = () => {
  const { isSuperAdmin } = useAuth();
  const [period, setPeriod] = useState<PeriodPreset>('30d');
  const [agent, setAgent] = useState<IaUsageAgent | ''>('');
  const [tier, setTier] = useState<ModelTier | ''>('');
  const [orgOverride, setOrgOverride] = useState<string>('');
  const [data, setData] = useState<UsageQueryResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const range = useMemo(() => periodRange(period), [period]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getUsageStats({
      from: range.from,
      to: range.to,
      agent: agent || undefined,
      tier: tier || undefined,
      organizationId: isSuperAdmin && orgOverride ? orgOverride : undefined,
    })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [period, agent, tier, orgOverride, isSuperAdmin, range.from, range.to]);

  async function handleExport() {
    try {
      const blob = await exportUsageCsv({
        from: range.from,
        to: range.to,
        agent: agent || undefined,
        tier: tier || undefined,
        organizationId: isSuperAdmin && orgOverride ? orgOverride : undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ia-usage-${range.from.slice(0, 10)}_${range.to.slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao exportar CSV');
    }
  }

  const stats = data?.stats;

  return (
    <div style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Filtros */}
      <div
        style={{
          display: 'flex',
          gap: 14,
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          background: '#fff',
          padding: 14,
          border: '1px solid #e2e8f0',
          borderRadius: 10,
        }}
      >
        <div>
          <div style={labelStyle}>Período</div>
          <select style={inputStyle} value={period} onChange={(e) => setPeriod(e.target.value as PeriodPreset)}>
            <option value="7d">Últimos 7 dias</option>
            <option value="30d">Últimos 30 dias</option>
          </select>
        </div>
        <div>
          <div style={labelStyle}>Agente</div>
          <select style={inputStyle} value={agent} onChange={(e) => setAgent(e.target.value as IaUsageAgent | '')}>
            <option value="">Todos</option>
            {AGENT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <div style={labelStyle}>Tier</div>
          <select style={inputStyle} value={tier} onChange={(e) => setTier(e.target.value as ModelTier | '')}>
            <option value="">Todos</option>
            {TIER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        {isSuperAdmin && (
          <div>
            <div style={labelStyle}>Organização (super-admin)</div>
            <input
              style={{ ...inputStyle, width: 220 }}
              value={orgOverride}
              onChange={(e) => setOrgOverride(e.target.value)}
              placeholder="organizationId — vazio = sua org"
            />
          </div>
        )}
        <div style={{ flex: 1 }} />
        <button
          onClick={handleExport}
          disabled={loading || !!error}
          style={{
            padding: '8px 14px',
            background: '#6366f1',
            color: '#fff',
            border: 0,
            borderRadius: 6,
            fontWeight: 700,
            fontSize: 13,
            cursor: loading || error ? 'not-allowed' : 'pointer',
            opacity: loading || error ? 0.6 : 1,
          }}
        >
          ⬇ Export CSV
        </button>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 8, fontSize: 13 }}>
          {error}
        </div>
      )}
      {loading && !error && (
        <div style={{ color: '#64748b', fontSize: 13 }}>Carregando…</div>
      )}

      {/* KPI cards */}
      {stats && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={cardStyle}>
            <div style={kpiLabel}>Chamadas</div>
            <div style={kpiValue}>{fmtInt(stats.totalCalls)}</div>
          </div>
          <div style={cardStyle}>
            <div style={kpiLabel}>Tokens (in / out)</div>
            <div style={kpiValue}>
              {fmtInt(stats.totalInputTokens)} / {fmtInt(stats.totalOutputTokens)}
            </div>
          </div>
          <div style={cardStyle}>
            <div style={kpiLabel}>Custo bruto provedor</div>
            <div style={kpiValue}>{fmtUsd(stats.totalCostProviderUsd)}</div>
          </div>
          <div style={cardStyle}>
            <div style={kpiLabel}>Valor cobrado (c/ markup)</div>
            <div style={kpiValue}>{fmtUsd(stats.totalCostBilledUsd)}</div>
          </div>
        </div>
      )}

      {/* By day (placeholder for chart — table for now) */}
      {stats && stats.byDay.length > 0 && (
        <div>
          <div style={{ ...kpiLabel, marginBottom: 8 }}>Consumo diário</div>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Dia</th>
                <th style={thStyle}>Chamadas</th>
                <th style={thStyle}>Custo cobrado</th>
              </tr>
            </thead>
            <tbody>
              {stats.byDay.map((d) => (
                <tr key={d.date}>
                  <td style={tdStyle}>{d.date}</td>
                  <td style={tdStyle}>{fmtInt(d.calls)}</td>
                  <td style={tdStyle}>{fmtUsd(d.costBilledUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* By agent */}
      {stats && stats.byAgent.length > 0 && (
        <div>
          <div style={{ ...kpiLabel, marginBottom: 8 }}>Por agente</div>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Agente</th>
                <th style={thStyle}>Chamadas</th>
                <th style={thStyle}>Tokens (in / out)</th>
                <th style={thStyle}>Custo cobrado</th>
              </tr>
            </thead>
            <tbody>
              {stats.byAgent.map((r) => (
                <tr key={r.agent}>
                  <td style={tdStyle}>{r.agent}</td>
                  <td style={tdStyle}>{fmtInt(r.calls)}</td>
                  <td style={tdStyle}>{fmtInt(r.inputTokens)} / {fmtInt(r.outputTokens)}</td>
                  <td style={tdStyle}>{fmtUsd(r.costBilledUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Top calls */}
      {stats && stats.topCalls.length > 0 && (
        <div>
          <div style={{ ...kpiLabel, marginBottom: 8 }}>Top 20 chamadas mais caras</div>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Quando</th>
                <th style={thStyle}>Agente</th>
                <th style={thStyle}>Modelo</th>
                <th style={thStyle}>Tokens</th>
                <th style={thStyle}>Latência</th>
                <th style={thStyle}>Custo</th>
              </tr>
            </thead>
            <tbody>
              {stats.topCalls.map((e) => (
                <tr key={e.callId}>
                  <td style={tdStyle}>{e.timestamp.replace('T', ' ').slice(0, 19)}</td>
                  <td style={tdStyle}>{e.agent}</td>
                  <td style={tdStyle}>{e.provider} / {e.model}</td>
                  <td style={tdStyle}>{fmtInt(e.inputTokens + e.outputTokens)}</td>
                  <td style={tdStyle}>{e.latencyMs}ms</td>
                  <td style={tdStyle}>{fmtUsd(e.costBilledUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {stats && stats.totalCalls === 0 && !loading && !error && (
        <div style={{ color: '#64748b', fontSize: 13, textAlign: 'center', padding: 40 }}>
          Nenhuma chamada IA registrada no período selecionado.
        </div>
      )}
    </div>
  );
};
